import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { ChatMessage, ISession, ThinkingStep, mapInteractionsToMessages } from '../api/chat/models'
import { getUpgradeMessageByType } from '../../components/organisms/ChatWidget/messages/mockMessages'
import { readCopaSessionId, writeCopaSessionId } from '../constants/storageKeys'

export type MessageType =
  | 'text'
  | 'upgrade'
  | 'area-limit'
  | 'white-label'
  | 'export-layers'
  | 'create-project'
  | 'miombo-report'

export interface CreateProjectFormData {
  name: string
  userContext?: string
  drawnFeatures?: GeoJSON.Feature[] | null
}

export interface ExtendedChatMessage extends ChatMessage {
  id: string
  date: string
  isError?: boolean
  isLoading?: boolean
  hideElapsed?: boolean
  type?: MessageType
  citations?: string[]
  thinkingSteps?: ThinkingStep[]
  thinkingDuration?: string
  metadata?: {
    currentUsers: number
    maxUsers: number
  }
}

// Chat store state interface
interface ChatState {
  messages: ExtendedChatMessage[]
  selectedCountry: string
  isFullScreen: boolean
  isExpanded: boolean
  isProjectMode: boolean
  sessionId: string | null
  isLoadingSessions: boolean
  /* Streaming generation (used by flows outside ChatWidget, e.g. proactive Copa response) */
  isGenerating: boolean
  generationAbortController: AbortController | null
  /* Create project form data */
  createProjectFormData: CreateProjectFormData | null
}

const lastAssistantIndex = (messages: ExtendedChatMessage[]): number => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') return i
  }
  return -1
}

// Chat store actions interface
interface ChatActions {
  addMessage: (message: ExtendedChatMessage) => void
  updateLastAssistantMessage: (chunk: string) => void
  updateLastAssistantThinking: (step: ThinkingStep) => void
  setLastAssistantThinkingDone: (durationMessage: string) => void
  removeLastAssistantMessage: (messageId: string) => void
  removeMessageByType: (type: MessageType) => void
  addErrorMessage: (error: any) => void
  clearMessages: () => void
  loadSessionFromData: (session: ISession) => void
  setIsLoadingSessions: (loading: boolean) => void
  startGeneration: (abortController: AbortController) => void
  stopGeneration: () => void
  upsertMessageByType: (type: MessageType, metadata?: Partial<ExtendedChatMessage['metadata']>) => void
  clearSystemMessages: () => void
  setSelectedCountry: (country: string) => void
  setIsFullScreen: (isFullScreen: boolean) => void
  setIsExpanded: (isExpanded: boolean) => void
  setProjectMode: (isProjectMode: boolean) => void
  setSessionId: (sessionId: string | null) => void
  /* Create project form actions */
  setCreateProjectFormData: (data: Partial<CreateProjectFormData> | null) => void
}

// Combined store interface
type ChatStore = ChatState & ChatActions

// Create store with selective persistence
export const useChatStore = create<ChatStore>()(
  persist(
    (set) => ({
      messages: [],
      selectedCountry: '', // Default is empty (global)
      isFullScreen: false,
      isExpanded: false,
      isProjectMode: false,
      sessionId: readCopaSessionId(), // each tab has its own session
      isLoadingSessions: false,
      isGenerating: false,
      generationAbortController: null,
      createProjectFormData: null,

      setIsExpanded: (isExpanded) => set({ isExpanded }),
      setIsFullScreen: (isFullScreen) => set({ isFullScreen }),

      addMessage: (message) =>
        set((state) => ({
          messages: [...state.messages, message],
        })),

      updateLastAssistantMessage: (chunk) =>
        set((state) => {
          const messages = [...state.messages]
          const i = lastAssistantIndex(messages)
          if (i !== -1) {
            messages[i] = { ...messages[i], content: messages[i].content + chunk, isLoading: false }
          }
          return { messages }
        }),

      updateLastAssistantThinking: (step) =>
        set((state) => {
          const messages = [...state.messages]
          const i = lastAssistantIndex(messages)
          if (i === -1) return {}

          const steps = messages[i].thinkingSteps ?? []
          const existing = steps.findIndex((s) => s.step === step.step)
          const nextSteps = existing === -1 ? [...steps, step] : steps.map((s, idx) => (idx === existing ? step : s))

          messages[i] = { ...messages[i], thinkingSteps: nextSteps }
          return { messages }
        }),

      setLastAssistantThinkingDone: (durationMessage) =>
        set((state) => {
          const messages = [...state.messages]
          const i = lastAssistantIndex(messages)
          if (i === -1) return {}

          messages[i] = { ...messages[i], thinkingDuration: durationMessage }
          return { messages }
        }),

      removeLastAssistantMessage: (messageId) =>
        set((state) => ({
          messages: state.messages.filter((msg) => msg.id !== messageId),
        })),

      removeMessageByType: (type) =>
        set((state) => ({
          messages: state.messages.filter((msg) => msg.type !== type),
        })),

      addErrorMessage: (error) => {
        let errorMessage = 'An error occurred while processing your request.'

        try {
          if (error?.message) {
            errorMessage = error.message
          } else if (typeof error === 'string') {
            errorMessage = error
          }
        } catch (e) {
          // Default error message is used
        }

        set((state) => {
          const messages = [...state.messages]

          if (messages.length > 0) {
            const lastIndex = messages.length - 1
            if (messages[lastIndex].role === 'assistant' && !messages[lastIndex].content.trim()) {
              messages.pop()
            }
          }

          return {
            messages: [
              ...messages,
              {
                id: Date.now().toString(),
                role: 'assistant',
                content: errorMessage,
                date: new Date().toISOString(),
                isError: true,
              },
            ],
          }
        })
      },

      clearMessages: () => {
        writeCopaSessionId(null)
        set({ messages: [], sessionId: null })
      },

      loadSessionFromData: (session) => {
        writeCopaSessionId(session.id)
        set({
          messages: mapInteractionsToMessages(session.interactions),
          sessionId: session.id,
        })
      },

      setIsLoadingSessions: (isLoadingSessions) => set({ isLoadingSessions }),

      startGeneration: (abortController) => set({ isGenerating: true, generationAbortController: abortController }),

      stopGeneration: () =>
        set((state) => {
          state.generationAbortController?.abort()
          return { isGenerating: false, generationAbortController: null }
        }),

      upsertMessageByType: (type: MessageType, metadata?: Partial<ExtendedChatMessage['metadata']>) =>
        set((state) => {
          const message = getUpgradeMessageByType(type, metadata)

          if (!message) {
            return {}
          }

          const latestMessage = state.messages[state.messages.length - 1]

          if (!latestMessage) {
            return { messages: [message] }
          }

          const baseMessages =
            !latestMessage.type || latestMessage.type === 'text' ? state.messages : state.messages.slice(0, -1)

          return { messages: [...baseMessages, message] }
        }),

      clearSystemMessages: () =>
        set((state) => ({
          messages: state.messages.filter((msg) => !msg.type || msg.type === 'text'),
        })),

      setSelectedCountry: (country) => set({ selectedCountry: country }),

      setProjectMode: (isProjectMode) => set({ isProjectMode }),

      setSessionId: (sessionId) => {
        writeCopaSessionId(sessionId)
        set({ sessionId })
      },

      setCreateProjectFormData: (data: Partial<CreateProjectFormData> | null) =>
        set((state) => {
          if (data === null) {
            return { createProjectFormData: null }
          }

          if (state.createProjectFormData) {
            return {
              createProjectFormData: {
                ...state.createProjectFormData,
                ...data,
              },
            }
          }

          return {
            createProjectFormData: data as CreateProjectFormData,
          }
        }),
    }),
    {
      name: 'chat-storage',
      // createProjectFormData is deliberately not persisted
      partialize: (state) => ({
        messages: state.messages,
        isFullScreen: state.isFullScreen,
        isExpanded: state.isExpanded,
      }),
      // each tab has its own session
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
)
